import React, { useRef, useEffect, useCallback, memo } from 'react';
import { StepType } from '../../../core/types/rust-engine-interfaces';
import type {
  ExecutionStep,
  ArrayStatePayload,
  ArrayItem,
} from '../../../core/types/rust-engine-interfaces';
import { drawMergeTree } from './sortingRenderers/drawMergeTree';
import { drawBubbleSort } from './sortingRenderers/drawBubbleSort';
import { drawSearch, isSearchAlgorithm } from './sortingRenderers/drawSearch';
import './roundRectPolyfill';

// ── Props ─────────────────────────────────────────────────────────────────────

export interface SortingVisualizerProps {
  currentStep: ExecutionStep | null;
  darkMode?: boolean;
  barWidth?: number;
  barGap?: number;
  height?: number;
  algorithmId?: string;
  allSteps?: ExecutionStep[];
  stepIndex?: number;
}

// ── Layout constants ──────────────────────────────────────────────────────────

const PAD_BOTTOM = 38;
const PAD_TOP    = 32;
const PAD_SIDE   = 14;

/** Maximum number of bars for which value + index labels are drawn. */
const MAX_ITEMS_WITH_LABELS = 100;

/** Maximum number of bars for which glow annotations are rendered.
 *  Above this threshold the shadow / stroke operations are too expensive. */
const MAX_ITEMS_WITH_ANNOTATIONS = 100;

// ── Color palette ─────────────────────────────────────────────────────────────

const COLOR_SORTED    = '#22c55e';
const COLOR_HEAP      = '#22d3ee';
const COLOR_PIVOT     = '#a855f7';
const COLOR_COMPARE   = '#fbbf24';
const COLOR_SWAP      = '#f87171';
const COLOR_ACTIVE    = '#fb923c';
const COLOR_GLOW      = '#f59e0b';
const COLOR_INDEX_HI  = '#fbbf24';
const COLOR_INDEX_LO  = '#334155';
const COLOR_VAL_LABEL = '#94a3b8';

// ── 5-stop gradient: deep teal → cyan → sky → indigo tint ─────────────────────
// Returns a per-bar vertical LinearGradient that deepens at the bar top and
// fades at the base, giving each bar real physical depth.

type RGB = [number, number, number];

const GRAD_STOPS: { pos: number; rgb: RGB }[] = [
  { pos: 0.00, rgb: [14,  116, 144] }, // #0e7490  teal-700   (low value, dark)
  { pos: 0.30, rgb: [8,   145, 178] }, // #0891b2  cyan-600
  { pos: 0.60, rgb: [34,  211, 238] }, // #22d3ee  cyan-400
  { pos: 0.82, rgb: [56,  189, 248] }, // #38bdf8  sky-400
  { pos: 1.00, rgb: [99,  230, 255] }, // #63e6ff  bright sky (high value, vivid)
];

function gradientColorAt(norm: number): string {
  const t = Math.max(0, Math.min(1, norm));
  for (let i = 1; i < GRAD_STOPS.length; i++) {
    const lo = GRAD_STOPS[i - 1]!;
    const hi = GRAD_STOPS[i]!;
    if (t <= hi.pos) {
      const f = (t - lo.pos) / (hi.pos - lo.pos);
      const r = Math.round(lo.rgb[0] + f * (hi.rgb[0] - lo.rgb[0]));
      const g = Math.round(lo.rgb[1] + f * (hi.rgb[1] - lo.rgb[1]));
      const b = Math.round(lo.rgb[2] + f * (hi.rgb[2] - lo.rgb[2]));
      return `rgb(${r},${g},${b})`;
    }
  }
  return `rgb(${GRAD_STOPS[GRAD_STOPS.length - 1]!.rgb.join(',')})`;
}

function makeBarGradient(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  bh: number,
  norm: number,
): CanvasGradient {
  const grad = ctx.createLinearGradient(x, y, x, y + bh);
  const col  = gradientColorAt(norm);
  // Top of bar: full vivid; bottom: 40% darkened
  grad.addColorStop(0,   col);
  grad.addColorStop(0.5, gradientColorAt(norm * 0.85));
  grad.addColorStop(1,   gradientColorAt(norm * 0.45));
  return grad;
}

// ── Bar state resolver ─────────────────────────────────────────────────────────
// Priority: sorted > heapified > pivot > compare/highlight > swap/current > active > gradient

type BarState = 'sorted' | 'heap' | 'pivot' | 'compare' | 'swap' | 'active' | 'default';

function resolveBarState(
  index: number,
  item: ArrayItem | undefined,
  metadata: ArrayStatePayload['metadata'],
  activeSet: Set<number>,
): BarState {
  if (metadata.sortedIndices?.includes(index) || item?.sorted) return 'sorted';
  if (metadata.heapifiedIndices?.includes(index)) return 'heap';
  if (metadata.pivotIndex === index) return 'pivot';

  const inCompare = (metadata.compareIndices?.includes(index) ?? false) ||
                    (metadata.highlightIndices?.includes(index) ?? false) ||
                    metadata.compareIndex === index ||
                    item?.compared || item?.highlighted;
  if (inCompare) return 'compare';

  if (item?.swapped) return 'swap';
  if (metadata.currentIndex === index || activeSet.has(index)) return 'active';
  return 'default';
}

function flatColor(state: BarState): string | null {
  switch (state) {
    case 'sorted':  return COLOR_SORTED;
    case 'heap':    return COLOR_HEAP;
    case 'pivot':   return COLOR_PIVOT;
    case 'compare': return COLOR_COMPARE;
    case 'swap':    return COLOR_SWAP;
    case 'active':  return COLOR_ACTIVE;
    default:        return null;
  }
}

// ── Pure canvas drawing helpers (no hooks, no closure captures) ─────────────

interface DrawLayout {
  barWidth: number;
  barGap: number;
  startX: number;
  canvasHeight: number;
  totalH: number;
  minVal: number;
  maxVal: number;
  range: number;
  baselineY: number;
  values: number[];
  activeSet: Set<number>;
  metadata: ArrayStatePayload['metadata'];
}

function drawBars(
  ctx: CanvasRenderingContext2D,
  items: ArrayItem[],
  barWidth: number,
  _totalH: number,
  layout: DrawLayout,
): void {
  const { barGap, startX, baselineY, range, totalH, values, activeSet, metadata } = layout;
  const bw = barWidth;
  const useRound = bw >= 6;

  items.forEach((_item: ArrayItem, idx: number) => {
    try {
      const item  = items[idx]!;
      const value = values[idx] ?? 0;
      const yBar  = baselineY - (value / range) * totalH;
      const y     = Math.min(baselineY, yBar);
      const bh    = Math.max(3, Math.abs(baselineY - yBar));
      const x     = startX + idx * (bw + barGap);
      const norm  = Math.abs(value) / Math.max(1, Math.abs(layout.maxVal), Math.abs(layout.minVal));

      const barState = resolveBarState(idx, item, metadata, activeSet);
      const fc       = flatColor(barState);

      if (fc) {
        ctx.fillStyle = fc;
      } else {
        ctx.fillStyle = makeBarGradient(ctx, x, y, bh, norm);
      }

      const radii: [number, number, number, number] = value >= 0 ? [4, 4, 0, 0] : [0, 0, 4, 4];
      ctx.beginPath();
      if (useRound) ctx.roundRect(x, y, bw, bh, radii);
      else          ctx.rect(x, y, bw, bh);
      ctx.fill();

      if (barState === 'default' && bw >= 8) {
        ctx.fillStyle = 'rgba(255,255,255,0.14)';
        const shineY = value >= 0 ? y + 1 : y + bh - 3;
        ctx.fillRect(x + 1, shineY, bw - 2, 2);
      }
    } catch (barErr) {
      console.warn(`[SortingVisualizer] Skipped bar ${idx}:`, barErr);
    }
  });
}

function drawLabels(
  ctx: CanvasRenderingContext2D,
  items: ArrayItem[],
  barWidth: number,
  layout: DrawLayout,
): void {
  if (items.length > MAX_ITEMS_WITH_LABELS) return;
  const { barGap, startX, canvasHeight: H, totalH, baselineY, range, values, activeSet } = layout;
  const bw = barWidth;
  const valFontSize = Math.min(11, Math.max(8, bw * 0.38));
  const idxFontSize = Math.min(10, Math.max(7, bw * 0.32));

  items.forEach((_item: ArrayItem, idx: number) => {
    try {
      const value = values[idx] ?? 0;
      const yBar  = baselineY - (value / range) * totalH;
      const y     = Math.min(baselineY, yBar);
      const bh    = Math.max(3, Math.abs(baselineY - yBar));
      const x     = startX + idx * (bw + barGap);

      if (bw >= 14) {
        ctx.fillStyle    = COLOR_VAL_LABEL;
        ctx.font         = `${valFontSize}px monospace`;
        ctx.textAlign    = 'center';
        ctx.textBaseline = value >= 0 ? 'bottom' : 'top';
        const labelY = value >= 0 ? y - 2 : y + bh + 2;
        ctx.fillText(String(value), x + bw / 2, labelY);
      }

      ctx.fillStyle    = activeSet.has(idx) ? COLOR_INDEX_HI : COLOR_INDEX_LO;
      ctx.font         = `${idxFontSize}px monospace`;
      ctx.textAlign    = 'center';
      ctx.textBaseline = 'top';
      ctx.fillText(String(idx), x + bw / 2, H - PAD_BOTTOM + 5);
    } catch (labelErr) {
      console.warn(`[SortingVisualizer] Skipped label ${idx}:`, labelErr);
    }
  });
}

function drawAnnotations(
  ctx: CanvasRenderingContext2D,
  step: ExecutionStep,
  barWidth: number,
  layout: DrawLayout,
): void {
  const { barGap, startX, baselineY, totalH, range, values, activeSet } = layout;
  const bw = barWidth;
  if (step.payload.type !== 'array') return;
  const { items, metadata } = step.payload as ArrayStatePayload;
  if (!Array.isArray(items) || items.length === 0) return;

  // Focal glow and pointer highlights
  if (activeSet.size > 0) {
    ctx.save();
    ctx.shadowColor = COLOR_GLOW;
    ctx.shadowBlur  = 24;
    ctx.strokeStyle = COLOR_GLOW;
    ctx.lineWidth   = 2;
    const useRound = bw >= 6;

    activeSet.forEach((idx) => {
      const value = values[idx] ?? 0;
      const yBar  = baselineY - (value / range) * totalH;
      const y     = Math.min(baselineY, yBar);
      const bh    = Math.max(3, Math.abs(baselineY - yBar));
      const x     = startX + idx * (bw + barGap);
      const radii: [number, number, number, number] = value >= 0 ? [6, 6, 0, 0] : [0, 0, 6, 6];
      ctx.beginPath();
      if (useRound) ctx.roundRect(x - 2, y - 2, bw + 4, bh + 4, radii);
      else          ctx.rect(x - 2, y - 2, bw + 4, bh + 4);
      ctx.stroke();
    });
    ctx.restore();

    const ptrH = Math.min(12, Math.max(7, bw * 0.35));
    const ptrW = ptrH * 1.25;

    ctx.fillStyle = COLOR_GLOW;
    ctx.save();
    ctx.shadowColor = COLOR_GLOW;
    ctx.shadowBlur  = 10;

    activeSet.forEach((idx) => {
      const value = values[idx] ?? 0;
      const yBar  = baselineY - (value / range) * totalH;
      const y     = Math.min(baselineY, yBar);
      const x     = startX + idx * (bw + barGap);
      const pcx   = x + bw / 2;
      const pcy   = value >= 0 ? y - 5 : Math.max(baselineY, yBar) + 5;
      ctx.beginPath();
      if (value >= 0) {
        ctx.moveTo(pcx - ptrW, pcy - ptrH);
        ctx.lineTo(pcx + ptrW, pcy - ptrH);
      } else {
        ctx.moveTo(pcx - ptrW, pcy + ptrH);
        ctx.lineTo(pcx + ptrW, pcy + ptrH);
      }
      ctx.lineTo(pcx, pcy);
      ctx.closePath();
      ctx.fill();
    });
    ctx.restore();
  }

  // Annotation chips
  const chipY = 6;
  let chipX   = PAD_SIDE;

  const drawChip = (label: string, color: string) => {
    ctx.font         = 'bold 10px monospace';
    ctx.textAlign    = 'left';
    ctx.textBaseline = 'top';
    const tw   = ctx.measureText(label).width;
    const chipW = tw + 16;
    const chipH = 19;
    ctx.fillStyle = `${color}18`;
    ctx.beginPath();
    ctx.roundRect(chipX, chipY, chipW, chipH, 5);
    ctx.fill();
    ctx.strokeStyle = color;
    ctx.lineWidth   = 1;
    ctx.beginPath();
    ctx.roundRect(chipX, chipY, chipW, chipH, 5);
    ctx.stroke();
    ctx.fillStyle = color;
    ctx.fillText(label, chipX + 8, chipY + 4);
    chipX += chipW + 8;
  };

  if (metadata.pivotIndex !== undefined && metadata.pivotIndex >= 0 && metadata.pivotIndex < items.length) {
    drawChip(`Pivot [${metadata.pivotIndex}] = ${items[metadata.pivotIndex]?.value ?? '?'}`, '#a855f7');
  }
  if (metadata.compareIndex !== undefined && metadata.compareIndex >= 0 && metadata.compareIndex < items.length) {
    drawChip(`Cmp [${metadata.compareIndex}] = ${items[metadata.compareIndex]?.value ?? '?'}`, '#fbbf24');
  }
  if (metadata.currentIndex !== undefined && metadata.currentIndex >= 0 && metadata.currentIndex < items.length) {
    drawChip(`Cur [${metadata.currentIndex}]`, '#fb923c');
  }
}

// ── Algorithm-specific overlays ───────────────────────────────────────────────

type ArrayAlgoKind = 'fenwick' | 'sieve' | 'gcd' | 'fibonacci' | 'dp' | 'skiplist' | 'dsu' | 'segment-tree' | 'sqrt-decomp' | 'mo-algo' | 'bloom'
  | 'activity' | 'frac-knapsack' | 'huffman' | 'job-seq'
  | 'fast-exp' | 'miller-rabin' | 'chinese-remainder' | 'mod-arith'
  | 'generic';

type SortSignature =
  | 'bubble' | 'cocktail' | 'selection' | 'insertion' | 'quick' | 'merge'
  | 'heap' | 'shell' | 'counting' | 'radix' | 'cycle' | 'comb' | 'gnome'
  | 'bitonic' | 'odd-even' | 'bucket' | 'pigeonhole' | 'stooge' | 'tim'
  | 'library' | 'binary-search' | 'jump-search' | 'interpolation-search'
  | 'exponential-search' | 'ternary-search' | 'fibonacci-search'
  | 'none';

function detectSortSignature(algorithmId: string): SortSignature {
  const id = algorithmId.toLowerCase();
  if (id.includes('bubble') && !id.includes('cocktail')) return 'bubble';
  if (id.includes('cocktail')) return 'cocktail';
  if (id.includes('selection')) return 'selection';
  if (id.includes('insertion') && !id.includes('library')) return 'insertion';
  if (id.includes('quick')) return 'quick';
  if (id.includes('merge')) return 'merge';
  if (id.includes('heap')) return 'heap';
  if (id.includes('shell')) return 'shell';
  if (id.includes('counting')) return 'counting';
  if (id.includes('radix')) return 'radix';
  if (id.includes('cycle')) return 'cycle';
  if (id.includes('comb')) return 'comb';
  if (id.includes('gnome')) return 'gnome';
  if (id.includes('bitonic')) return 'bitonic';
  if (id.includes('odd-even')) return 'odd-even';
  if (id.includes('bucket')) return 'bucket';
  if (id.includes('pigeonhole')) return 'pigeonhole';
  if (id.includes('stooge')) return 'stooge';
  if (id.includes('tim')) return 'tim';
  if (id.includes('library')) return 'library';
  if (id.includes('binary-search')) return 'binary-search';
  if (id.includes('jump-search')) return 'jump-search';
  if (id.includes('interpolation')) return 'interpolation-search';
  if (id.includes('exponential')) return 'exponential-search';
  if (id.includes('ternary')) return 'ternary-search';
  if (id.includes('fibonacci-search')) return 'fibonacci-search';
  return 'none';
}

function detectArrayAlgo(explanation: string, algorithmId?: string): ArrayAlgoKind {
  const aid = (algorithmId ?? '').toLowerCase();
  if (aid.includes('dsu') || aid.includes('disjoint-set') || aid.includes('union-find')) return 'dsu';
  if (aid.includes('fenwick') || aid.includes('binary-indexed')) return 'fenwick';
  if (aid.includes('segment-tree')) return 'segment-tree';
  if (aid.includes('sqrt') || aid.includes('sqrt-decomp')) return 'sqrt-decomp';
  if (aid.includes('mo-algo') || aid.includes('mo-algorithm')) return 'mo-algo';
  if (aid.includes('bloom')) return 'bloom';
  if (aid.includes('skip-list') || aid.includes('skiplist')) return 'skiplist';
  if (aid.includes('activity')) return 'activity';
  if (aid.includes('fractional-knapsack') || aid.includes('fractional-knap')) return 'frac-knapsack';
  if (aid.includes('huffman')) return 'huffman';
  if (aid.includes('job-sequencing') || aid.includes('job-seq')) return 'job-seq';
  if (aid.includes('fast-exp') || aid.includes('fast-exponentiation')) return 'fast-exp';
  if (aid.includes('miller') || aid.includes('rabin')) return 'miller-rabin';
  if (aid.includes('chinese') || aid.includes('crt')) return 'chinese-remainder';
  if (aid.includes('modular') || aid.includes('mod-arith')) return 'mod-arith';
  const txt = explanation.toLowerCase();
  if (txt.includes('fenwick') || txt.includes('tree[') || txt.includes('prefix_sum')) return 'fenwick';
  if (txt.includes('sieve') || txt.includes('composite') || txt.includes('prime')) return 'sieve';
  if (txt.includes('gcd') || txt.includes('extendedgcd') || txt.includes('euclidean')) return 'gcd';
  if (txt.includes('skip list') || txt.includes('skip-list')) return 'skiplist';
  if (txt.includes('fibonacci') || txt.includes('f(')) return 'fibonacci';
  if (txt.includes('coin change') || txt.includes('lis') || txt.includes('partition') || txt.includes('word break') || txt.includes('dp[') || txt.includes('subsequence')) return 'dp';
  if (txt.includes('dsu') || txt.includes('disjoint set') || txt.includes('union(')) return 'dsu';
  if (txt.includes('segment tree') || txt.includes('tree[') || txt.includes('merge: tree[')) return 'segment-tree';
  if (txt.includes('sqrt') || txt.includes('block')) return 'sqrt-decomp';
  if (txt.includes("mo's") || txt.includes('mo algorithm') || txt.includes('pointer')) return 'mo-algo';
  return 'generic';
}

function drawFenwickArrows(
  ctx: CanvasRenderingContext2D,
  explanation: string,
  layout: DrawLayout,
): void {
  const { barGap, startX, baselineY, totalH, range, values } = layout;
  const bw = layout.barWidth;

  // Parse "tree[X] += delta = val" or "tree[X]=val" from explanation
  const treeMatch = explanation.match(/tree\[(\d+)\]/g);
  if (!treeMatch || treeMatch.length < 2) return;

  // Extract indices from all tree[N] references
  const indices = treeMatch.map(m => parseInt(m.match(/\d+/)![0], 10));
  if (indices.length < 2) return;

  // Draw arrows between consecutive tree indices (LSB jumps)
  ctx.save();
  ctx.strokeStyle = '#22d3ee';
  ctx.fillStyle = '#22d3ee';
  ctx.lineWidth = 1.5;
  ctx.setLineDash([4, 3]);

  for (let i = 0; i < indices.length - 1; i++) {
    const fromIdx = indices[i]! - 1;  // convert to 0-based
    const toIdx = indices[i + 1]! - 1;
    if (fromIdx < 0 || toIdx < 0 || fromIdx >= values.length || toIdx >= values.length) continue;

    const fromVal = values[fromIdx] ?? 0;
    const toVal = values[toIdx] ?? 0;
    const fromY = baselineY - (fromVal / range) * totalH;
    const toY = baselineY - (toVal / range) * totalH;
    const fromX = startX + fromIdx * (bw + barGap) + bw / 2;
    const toX = startX + toIdx * (bw + barGap) + bw / 2;

    // Curved arrow above bars
    const midX = (fromX + toX) / 2;
    const arcH = 30;
    const midY = Math.min(fromY, toY) - arcH;

    ctx.beginPath();
    ctx.moveTo(fromX, fromY - 4);
    ctx.quadraticCurveTo(midX, midY, toX, toY - 4);
    ctx.stroke();

    // Arrowhead
    ctx.setLineDash([]);
    const angle = Math.atan2(toY - midY, toX - midX);
    ctx.beginPath();
    ctx.moveTo(toX, toY - 4);
    ctx.lineTo(toX - 6 * Math.cos(angle - 0.4), toY - 4 - 6 * Math.sin(angle - 0.4));
    ctx.lineTo(toX - 6 * Math.cos(angle + 0.4), toY - 4 - 6 * Math.sin(angle + 0.4));
    ctx.closePath();
    ctx.fill();
    ctx.setLineDash([4, 3]);
  }

  // Label the jump
  const jumpMatch = explanation.match(/step\s*=\s*(\d+)/);
  if (jumpMatch) {
    ctx.setLineDash([]);
    ctx.font = 'bold 9px monospace';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'bottom';
    ctx.fillStyle = '#22d3ee';
    const firstIdx = indices[0]! - 1;
    const x = startX + firstIdx * (bw + barGap) + bw / 2;
    ctx.fillText(`LSB=${jumpMatch[1]}`, x, baselineY - totalH - 8);
  }

  ctx.restore();
}

function drawSieveArcs(
  ctx: CanvasRenderingContext2D,
  explanation: string,
  layout: DrawLayout,
): void {
  const { barGap, startX, baselineY, totalH, range, values } = layout;
  const bw = layout.barWidth;

  // Parse "Marking X as composite (multiple of Y)" or "Y is prime. Marking..."
  const compositeMatch = explanation.match(/marking\s+(\d+)\s+as composite.*multiple of\s+(\d+)/i);
  const primeMatch = explanation.match(/(\d+)\s+is prime/i);

  if (compositeMatch) {
    const compositeIdx = parseInt(compositeMatch[1]!, 10);
    const primeIdx = parseInt(compositeMatch[2]!, 10);
    if (compositeIdx >= values.length || primeIdx >= values.length) return;

    const primeX = startX + primeIdx * (bw + barGap) + bw / 2;
    const compX = startX + compositeIdx * (bw + barGap) + bw / 2;
    const barY = baselineY - (values[compositeIdx]! / range) * totalH;

    ctx.save();
    ctx.strokeStyle = '#f87171';
    ctx.fillStyle = '#f87171';
    ctx.lineWidth = 1.5;
    ctx.setLineDash([3, 2]);

    const midX = (primeX + compX) / 2;
    const arcH = 25;
    const midY = Math.min(baselineY - totalH, barY) - arcH;

    ctx.beginPath();
    ctx.moveTo(primeX, baselineY - totalH - 2);
    ctx.quadraticCurveTo(midX, midY, compX, barY - 4);
    ctx.stroke();

    // Arrowhead
    ctx.setLineDash([]);
    const angle = Math.atan2(barY - 4 - midY, compX - midX);
    ctx.beginPath();
    ctx.moveTo(compX, barY - 4);
    ctx.lineTo(compX - 6 * Math.cos(angle - 0.4), barY - 4 - 6 * Math.sin(angle - 0.4));
    ctx.lineTo(compX - 6 * Math.cos(angle + 0.4), barY - 4 - 6 * Math.sin(angle + 0.4));
    ctx.closePath();
    ctx.fill();

    // Label
    ctx.font = 'bold 8px monospace';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'bottom';
    ctx.fillText(`×${Math.round(compositeIdx / primeIdx)}`, midX, midY - 2);
    ctx.restore();
  } else if (primeMatch) {
    const primeIdx = parseInt(primeMatch[1]!, 10);
    if (primeIdx >= values.length) return;

    const primeX = startX + primeIdx * (bw + barGap) + bw / 2;
    const barY = baselineY - (values[primeIdx]! / range) * totalH;

    ctx.save();
    ctx.strokeStyle = '#22c55e';
    ctx.fillStyle = '#22c55e';
    ctx.lineWidth = 2;

    // Glow ring around prime bar
    ctx.beginPath();
    ctx.arc(primeX, barY - 6, 8, 0, Math.PI * 2);
    ctx.stroke();

    ctx.font = 'bold 9px monospace';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'bottom';
    ctx.fillText('PRIME', primeX, barY - 18);
    ctx.restore();
  }
}

function drawGcdArrows(
  ctx: CanvasRenderingContext2D,
  explanation: string,
  layout: DrawLayout,
): void {
  const { barGap, startX, baselineY, totalH, range, values } = layout;
  const bw = layout.barWidth;

  // Parse "a mod b = r" or "Update: a=X, b=Y"
  const modMatch = explanation.match(/(\d+)\s*mod\s*(\d+)\s*=\s*(\d+)/i);
  const updateMatch = explanation.match(/update:\s*a=(\d+),\s*b=(\d+)/i);

  ctx.save();
  ctx.strokeStyle = '#a78bfa';
  ctx.fillStyle = '#a78bfa';
  ctx.lineWidth = 1.5;
  ctx.setLineDash([4, 3]);

  if (modMatch) {
    const a = parseInt(modMatch[1]!, 10);
    const b = parseInt(modMatch[2]!, 10);
    const r = parseInt(modMatch[3]!, 10);
    // Find indices of a, b, r in values
    const aIdx = values.indexOf(a);
    const bIdx = values.indexOf(b);
    const rIdx = values.indexOf(r);
    if (aIdx >= 0 && bIdx >= 0) {
      const aX = startX + aIdx * (bw + barGap) + bw / 2;
      const bX = startX + bIdx * (bw + barGap) + bw / 2;
      const aY = baselineY - (values[aIdx]! / range) * totalH;
      const bY = baselineY - (values[bIdx]! / range) * totalH;
      const midX = (aX + bX) / 2;
      const midY = Math.min(aY, bY) - 25;

      ctx.beginPath();
      ctx.moveTo(aX, aY - 4);
      ctx.quadraticCurveTo(midX, midY, bX, bY - 4);
      ctx.stroke();

      // Arrowhead
      ctx.setLineDash([]);
      const angle = Math.atan2(bY - 4 - midY, bX - midX);
      ctx.beginPath();
      ctx.moveTo(bX, bY - 4);
      ctx.lineTo(bX - 6 * Math.cos(angle - 0.4), bY - 4 - 6 * Math.sin(angle - 0.4));
      ctx.lineTo(bX - 6 * Math.cos(angle + 0.4), bY - 4 - 6 * Math.sin(angle + 0.4));
      ctx.closePath();
      ctx.fill();

      // Label "mod"
      ctx.font = 'bold 8px monospace';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'bottom';
      ctx.fillText(`mod`, midX, midY - 2);
      ctx.setLineDash([4, 3]);
    }

    if (rIdx >= 0 && bIdx >= 0) {
      const rX = startX + rIdx * (bw + barGap) + bw / 2;
      const bX = startX + bIdx * (bw + barGap) + bw / 2;
      const rY = baselineY - (values[rIdx]! / range) * totalH;
      const bY = baselineY - (values[bIdx]! / range) * totalH;
      const midX = (rX + bX) / 2;
      const midY = Math.min(rY, bY) - 20;

      ctx.beginPath();
      ctx.moveTo(bX, bY - 4);
      ctx.quadraticCurveTo(midX, midY, rX, rY - 4);
      ctx.stroke();

      ctx.setLineDash([]);
      const angle = Math.atan2(rY - 4 - midY, rX - midX);
      ctx.beginPath();
      ctx.moveTo(rX, rY - 4);
      ctx.lineTo(rX - 6 * Math.cos(angle - 0.4), rY - 4 - 6 * Math.sin(angle - 0.4));
      ctx.lineTo(rX - 6 * Math.cos(angle + 0.4), rY - 4 - 6 * Math.sin(angle + 0.4));
      ctx.closePath();
      ctx.fill();
      ctx.fillText(`r=${r}`, midX, midY - 2);
    }
  } else if (updateMatch) {
    const a = parseInt(updateMatch[1]!, 10);
    const b = parseInt(updateMatch[2]!, 10);
    const aIdx = values.indexOf(a);
    const bIdx = values.indexOf(b);
    if (aIdx >= 0 && bIdx >= 0 && aIdx !== bIdx) {
      const aX = startX + aIdx * (bw + barGap) + bw / 2;
      const bX = startX + bIdx * (bw + barGap) + bw / 2;
      const aY = baselineY - (values[aIdx]! / range) * totalH;
      const bY = baselineY - (values[bIdx]! / range) * totalH;

      // Draw swap arrow between a and b
      ctx.setLineDash([]);
      ctx.strokeStyle = '#f59e0b';
      ctx.fillStyle = '#f59e0b';
      ctx.beginPath();
      ctx.moveTo(aX, aY - 4);
      ctx.lineTo(bX, bY - 4);
      ctx.stroke();

      const angle = Math.atan2(bY - 4 - (aY - 4), bX - aX);
      ctx.beginPath();
      ctx.moveTo(bX, bY - 4);
      ctx.lineTo(bX - 8 * Math.cos(angle - 0.4), bY - 4 - 8 * Math.sin(angle - 0.4));
      ctx.lineTo(bX - 8 * Math.cos(angle + 0.4), bY - 4 - 8 * Math.sin(angle + 0.4));
      ctx.closePath();
      ctx.fill();

      ctx.font = 'bold 9px monospace';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'bottom';
      ctx.fillText('a←b', (aX + bX) / 2, Math.min(aY, bY) - 12);
    }
  }

  ctx.restore();
}

function drawDpDependencyArrows(
  ctx: CanvasRenderingContext2D,
  _explanation: string,
  layout: DrawLayout,
  metadata: ArrayStatePayload['metadata'],
): void {
  const { barGap, startX, baselineY, totalH, range, values } = layout;
  const bw = layout.barWidth;

  const currentIdx = metadata.currentIndex;
  const highlightIndices = metadata.highlightIndices ?? [];
  if (currentIdx === undefined || currentIdx < 0 || highlightIndices.length === 0) return;

  const targetX = startX + currentIdx * (bw + barGap) + bw / 2;
  const targetY = baselineY - ((values[currentIdx] ?? 0) / range) * totalH;

  ctx.save();
  ctx.strokeStyle = '#60a5fa';
  ctx.fillStyle = '#60a5fa';
  ctx.lineWidth = 1.5;
  ctx.setLineDash([3, 2]);

  for (const srcIdx of highlightIndices) {
    if (srcIdx === currentIdx || srcIdx < 0 || srcIdx >= values.length) continue;
    const srcX = startX + srcIdx * (bw + barGap) + bw / 2;
    const srcY = baselineY - ((values[srcIdx] ?? 0) / range) * totalH;

    const midX = (srcX + targetX) / 2;
    const arcH = 22;
    const midY = Math.min(srcY, targetY) - arcH;

    ctx.beginPath();
    ctx.moveTo(srcX, srcY - 4);
    ctx.quadraticCurveTo(midX, midY, targetX, targetY - 4);
    ctx.stroke();

    // Arrowhead
    ctx.setLineDash([]);
    const angle = Math.atan2(targetY - 4 - midY, targetX - midX);
    ctx.beginPath();
    ctx.moveTo(targetX, targetY - 4);
    ctx.lineTo(targetX - 6 * Math.cos(angle - 0.4), targetY - 4 - 6 * Math.sin(angle - 0.4));
    ctx.lineTo(targetX - 6 * Math.cos(angle + 0.4), targetY - 4 - 6 * Math.sin(angle + 0.4));
    ctx.closePath();
    ctx.fill();
    ctx.setLineDash([3, 2]);
  }

  ctx.restore();
}

function drawSkipListLevels(
  ctx: CanvasRenderingContext2D,
  explanation: string,
  layout: DrawLayout,
): void {
  const { barGap, startX, baselineY, totalH, values } = layout;
  const bw = layout.barWidth;

  // Parse "level=X" or "max level=X" from explanation
  const levelMatch = explanation.match(/level\s*=\s*(\d+)/i);
  const maxLevelMatch = explanation.match(/max level\s*=\s*(\d+)/i);
  const maxLevel = maxLevelMatch ? parseInt(maxLevelMatch[1]!, 10) : levelMatch ? parseInt(levelMatch[1]!, 10) : 0;
  if (maxLevel <= 0 && !levelMatch) return;

  ctx.save();
  ctx.strokeStyle = '#22d3ee';
  ctx.fillStyle = '#22d3ee';
  ctx.lineWidth = 0.8;
  ctx.font = 'bold 8px monospace';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';

  // Draw horizontal level lines across the bar area
  const levelCount = Math.min(maxLevel + 1, 5);
  for (let lvl = 0; lvl < levelCount; lvl++) {
    const y = baselineY - totalH - 4 - lvl * 14;
    if (y < 0) break;
    ctx.globalAlpha = 0.3 + (1 - lvl / levelCount) * 0.3;
    ctx.setLineDash(lvl === 0 ? [] : [2, 3]);
    ctx.beginPath();
    ctx.moveTo(startX, y);
    ctx.lineTo(startX + values.length * (bw + barGap), y);
    ctx.stroke();

    // Level label
    ctx.setLineDash([]);
    ctx.fillText(`L${lvl}`, startX - 12, y - 2);
  }

  // Highlight current insert level
  if (levelMatch) {
    const insertLevel = parseInt(levelMatch[1]!, 10);
    const y = baselineY - totalH - 4 - insertLevel * 14;
    ctx.globalAlpha = 0.8;
    ctx.strokeStyle = '#f59e0b';
    ctx.fillStyle = '#f59e0b';
    ctx.lineWidth = 1.5;
    ctx.setLineDash([]);
    ctx.beginPath();
    ctx.moveTo(startX, y);
    ctx.lineTo(startX + values.length * (bw + barGap), y);
    ctx.stroke();
    ctx.fillText(`L${insertLevel} ←`, startX + values.length * (bw + barGap) + 4, y - 2);
  }

  ctx.restore();
}

function drawSortSignature(
  ctx: CanvasRenderingContext2D,
  signature: SortSignature,
  step: ExecutionStep,
  layout: DrawLayout,
): void {
  if (step.payload.type !== 'array') return;
  const { barGap, startX, baselineY, totalH, range, values, metadata } = layout;
  const bw = layout.barWidth;
  const ap = step.payload as ArrayStatePayload;
  const items = ap.items;
  const n = items.length;
  const sortedIndices = metadata.sortedIndices ?? [];
  const compareIndices = metadata.compareIndices ?? [];
  const highlightIndices = metadata.highlightIndices ?? [];
  const swappedIndices = metadata.swappedIndices ?? [];
  const currentIdx = metadata.currentIndex;
  const stepType = step.stepType;

  const barCenter = (i: number) => startX + i * (bw + barGap) + bw / 2;
  const barTop = (i: number) => baselineY - ((values[i] ?? 0) / range) * totalH;

  ctx.save();
  ctx.font = 'bold 9px monospace';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  switch (signature) {
    case 'bubble': {
      // Draw a ">" sweep direction arrow under the comparison pair
      if (compareIndices.length >= 2 && stepType === StepType.Comparison) {
        const [a, b] = compareIndices;
        if (a !== undefined && b !== undefined && a < n && b < n) {
          const ax = barCenter(a), bx = barCenter(b);
          const y = baselineY + 14;
          ctx.strokeStyle = '#fbbf24';
          ctx.fillStyle = '#fbbf24';
          ctx.lineWidth = 1.5;
          ctx.beginPath();
          ctx.moveTo(ax, y);
          ctx.lineTo(bx, y);
          ctx.stroke();
          const ang = Math.atan2(0, bx - ax);
          ctx.beginPath();
          ctx.moveTo(bx, y);
          ctx.lineTo(bx - 6 * Math.cos(ang - 0.4), y - 6 * Math.sin(ang - 0.4));
          ctx.lineTo(bx - 6 * Math.cos(ang + 0.4), y - 6 * Math.sin(ang + 0.4));
          ctx.closePath();
          ctx.fill();
        }
      }
      break;
    }

    case 'cocktail': {
      // Bidirectional sweep: draw arrow showing direction (right→ or ←left)
      if (compareIndices.length >= 2 && stepType === StepType.Comparison) {
        const [a, b] = compareIndices;
        if (a !== undefined && b !== undefined && a < n && b < n) {
          const goingRight = b > a;
          const ax = barCenter(a), bx = barCenter(b);
          const y = baselineY + 14;
          ctx.strokeStyle = goingRight ? '#22d3ee' : '#f472b6';
          ctx.fillStyle = goingRight ? '#22d3ee' : '#f472b6';
          ctx.lineWidth = 1.5;
          ctx.beginPath();
          ctx.moveTo(ax, y);
          ctx.lineTo(bx, y);
          ctx.stroke();
          const dir = goingRight ? 1 : -1;
          const tipX = goingRight ? bx : ax;
          ctx.beginPath();
          ctx.moveTo(tipX, y);
          ctx.lineTo(tipX - 6 * dir, y - 4);
          ctx.lineTo(tipX - 6 * dir, y + 4);
          ctx.closePath();
          ctx.fill();
          ctx.fillText(goingRight ? '→' : '←', (ax + bx) / 2, y - 8);
        }
      }
      break;
    }

    case 'selection': {
      // Draw a bracket over the unsorted scan region
      if (currentIdx !== undefined && stepType === StepType.Decision) {
        const sortedEnd = sortedIndices.length;
        const y = 6;
        ctx.strokeStyle = '#fb923c';
        ctx.lineWidth = 1.5;
        const sx = startX + sortedEnd * (bw + barGap) - barGap / 2;
        const ex = startX + (n - 1) * (bw + barGap) + bw + barGap / 2;
        ctx.beginPath();
        ctx.moveTo(sx, y + 4);
        ctx.lineTo(sx, y);
        ctx.lineTo(ex, y);
        ctx.lineTo(ex, y + 4);
        ctx.stroke();
        ctx.fillStyle = '#fb923c';
        ctx.fillText('find min', (sx + ex) / 2, y + 10);
      }
      // Highlight current min with a star marker
      if (highlightIndices.length === 1 && stepType === StepType.Update) {
        const mi = highlightIndices[0];
        if (mi !== undefined && mi < n) {
          const x = barCenter(mi), y = barTop(mi) - 12;
          ctx.fillStyle = '#22c55e';
          ctx.font = 'bold 12px monospace';
          ctx.fillText('★', x, y);
        }
      }
      break;
    }

    case 'insertion': {
      // Draw a "key" badge above the current element and shift arrows
      if (currentIdx !== undefined && stepType === StepType.Decision) {
        const x = barCenter(currentIdx), y = barTop(currentIdx) - 14;
        ctx.fillStyle = '#8b5cf6';
        ctx.fillText('key', x, y);
      }
      if (swappedIndices.length >= 2 && stepType === StepType.Update) {
        const [a, b] = swappedIndices;
        if (a !== undefined && b !== undefined && a < n && b < n) {
          const ax = barCenter(a), bx = barCenter(b);
          const y = baselineY + 14;
          ctx.strokeStyle = '#8b5cf6';
          ctx.fillStyle = '#8b5cf6';
          ctx.lineWidth = 1.5;
          ctx.setLineDash([3, 2]);
          ctx.beginPath();
          ctx.moveTo(ax, y);
          ctx.lineTo(bx, y);
          ctx.stroke();
          ctx.setLineDash([]);
          ctx.fillText('shift→', (ax + bx) / 2, y - 8);
        }
      }
      break;
    }

    case 'quick': {
      // Draw partition boundary line
      if (metadata.pivotIndex !== undefined && stepType === StepType.Comparison) {
        const pIdx = metadata.pivotIndex;
        if (pIdx < n) {
          const px = startX + pIdx * (bw + barGap) + bw + barGap / 2;
          ctx.strokeStyle = '#a855f7';
          ctx.lineWidth = 1;
          ctx.setLineDash([4, 3]);
          ctx.beginPath();
          ctx.moveTo(px, baselineY - totalH - 4);
          ctx.lineTo(px, baselineY + 4);
          ctx.stroke();
          ctx.setLineDash([]);
          // "pivot" label already drawn by drawAnnotations chip
        }
      }
      // Draw "i" boundary marker
      if (currentIdx !== undefined && stepType === StepType.Update && swappedIndices.length >= 2) {
        const x = barCenter(currentIdx);
        ctx.fillStyle = '#a855f7';
        ctx.fillText('|i', x, baselineY + 14);
      }
      break;
    }

    case 'merge': {
      // Draw merge window bracket over the active subarray range
      if (compareIndices.length >= 2) {
        const [a, b] = compareIndices;
        if (a !== undefined && b !== undefined && a < n && b < n) {
          const y = 6;
          const sx = startX + Math.min(a, b) * (bw + barGap) - barGap / 2;
          const ex = startX + Math.max(a, b) * (bw + barGap) + bw + barGap / 2;
          ctx.strokeStyle = '#06b6d4';
          ctx.lineWidth = 1.5;
          ctx.beginPath();
          ctx.moveTo(sx, y + 4);
          ctx.lineTo(sx, y);
          ctx.lineTo(ex, y);
          ctx.lineTo(ex, y + 4);
          ctx.stroke();
          ctx.fillStyle = '#06b6d4';
          ctx.fillText('merge', (sx + ex) / 2, y + 10);
        }
      }
      break;
    }

    case 'heap': {
      // Draw parent-child tree relationship arrows during heapify
      if (compareIndices.length >= 2 && stepType === StepType.Comparison) {
        const [parent, child] = compareIndices;
        if (parent !== undefined && child !== undefined && parent < n && child < n) {
          const px = barCenter(parent), cx = barCenter(child);
          const py = barTop(parent), cy = barTop(child);
          ctx.strokeStyle = '#22d3ee';
          ctx.fillStyle = '#22d3ee';
          ctx.lineWidth = 1;
          ctx.setLineDash([2, 2]);
          ctx.beginPath();
          ctx.moveTo(px, py - 6);
          ctx.lineTo(cx, cy - 6);
          ctx.stroke();
          ctx.setLineDash([]);
          ctx.fillText('parent→child', (px + cx) / 2, Math.min(py, cy) - 14);
        }
      }
      break;
    }

    case 'shell': {
      // Draw gap connection lines between compared elements
      if (compareIndices.length >= 2 && stepType === StepType.Comparison) {
        const [a, b] = compareIndices;
        if (a !== undefined && b !== undefined && a < n && b < n) {
          const ax = barCenter(a), bx = barCenter(b);
          const ay = barTop(a), by = barTop(b);
          ctx.strokeStyle = '#e879f9';
          ctx.lineWidth = 1.5;
          ctx.setLineDash([5, 3]);
          ctx.beginPath();
          ctx.moveTo(ax, ay - 6);
          ctx.lineTo(bx, by - 6);
          ctx.stroke();
          ctx.setLineDash([]);
          const gap = Math.abs(b - a);
          ctx.fillStyle = '#e879f9';
          ctx.fillText(`gap=${gap}`, (ax + bx) / 2, Math.min(ay, by) - 14);
        }
      }
      break;
    }

    case 'counting': {
      // Draw count histogram mini-bars above the array
      if (currentIdx !== undefined && stepType === StepType.Comparison) {
        const x = barCenter(currentIdx);
        const y = barTop(currentIdx) - 16;
        ctx.fillStyle = '#10b981';
        ctx.fillText('count++', x, y);
      }
      break;
    }

    case 'radix': {
      // Draw current digit badge
      const expl = step.metadata?.explanation ?? '';
      const digitMatch = expl.match(/digit\s*#?\s*(\d+)/i);
      if (digitMatch && currentIdx !== undefined) {
        const x = barCenter(currentIdx);
        const y = barTop(currentIdx) - 16;
        ctx.fillStyle = '#f97316';
        ctx.fillText(`d${digitMatch[1]}`, x, y);
      }
      break;
    }

    case 'cycle': {
      // Draw circular rotation arrow
      if (currentIdx !== undefined && stepType === StepType.Decision) {
        const x = barCenter(currentIdx);
        const y = barTop(currentIdx) - 14;
        ctx.strokeStyle = '#ec4899';
        ctx.fillStyle = '#ec4899';
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.arc(x, y, 8, 0, Math.PI * 1.5);
        ctx.stroke();
        // Arrowhead at end of arc
        const ahx = x + 8 * Math.cos(Math.PI * 1.5);
        const ahy = y + 8 * Math.sin(Math.PI * 1.5);
        ctx.beginPath();
        ctx.moveTo(ahx, ahy);
        ctx.lineTo(ahx - 4, ahy - 4);
        ctx.lineTo(ahx + 2, ahy - 2);
        ctx.closePath();
        ctx.fill();
        ctx.fillText('cycle', x, y - 14);
      }
      break;
    }

    case 'comb': {
      // Draw gap connection similar to shell but with shrink factor
      if (compareIndices.length >= 2 && stepType === StepType.Comparison) {
        const [a, b] = compareIndices;
        if (a !== undefined && b !== undefined && a < n && b < n) {
          const ax = barCenter(a), bx = barCenter(b);
          const ay = barTop(a), by = barTop(b);
          ctx.strokeStyle = '#f59e0b';
          ctx.lineWidth = 1.5;
          ctx.setLineDash([6, 2]);
          ctx.beginPath();
          ctx.moveTo(ax, ay - 6);
          ctx.lineTo(bx, by - 6);
          ctx.stroke();
          ctx.setLineDash([]);
          ctx.fillStyle = '#f59e0b';
          ctx.fillText('comb', (ax + bx) / 2, Math.min(ay, by) - 14);
        }
      }
      break;
    }

    case 'gnome': {
      // Draw backstep arrow when moving left
      if (compareIndices.length >= 2 && stepType === StepType.Comparison) {
        const [a, b] = compareIndices;
        if (a !== undefined && b !== undefined && a < n && b < n) {
          const goingBack = b < a;
          const ax = barCenter(a), bx = barCenter(b);
          const y = baselineY + 14;
          ctx.strokeStyle = goingBack ? '#f472b6' : '#fbbf24';
          ctx.fillStyle = goingBack ? '#f472b6' : '#fbbf24';
          ctx.lineWidth = 1.5;
          ctx.beginPath();
          ctx.moveTo(ax, y);
          ctx.lineTo(bx, y);
          ctx.stroke();
          ctx.fillText(goingBack ? '←gnome' : 'gnome→', (ax + bx) / 2, y - 8);
        }
      }
      break;
    }

    case 'bitonic': {
      // Draw up/down direction indicators
      if (compareIndices.length >= 2 && stepType === StepType.Comparison) {
        const [a, b] = compareIndices;
        if (a !== undefined && b !== undefined && a < n && b < n) {
          const ax = barCenter(a), bx = barCenter(b);
          const y = baselineY + 14;
          ctx.fillStyle = '#3b82f6';
          ctx.fillText('⇅', (ax + bx) / 2, y);
        }
      }
      break;
    }

    case 'odd-even': {
      // Draw parity indicator (odd/even pair)
      if (compareIndices.length >= 2 && stepType === StepType.Comparison) {
        const [a] = compareIndices;
        if (a !== undefined && a < n) {
          const isEven = a % 2 === 0;
          ctx.fillStyle = isEven ? '#60a5fa' : '#c084fc';
          ctx.fillText(isEven ? 'even' : 'odd', barCenter(a), baselineY + 14);
        }
      }
      break;
    }

    case 'bucket': {
      // Draw bucket grouping brackets
      if (stepType === StepType.Initialization || stepType === StepType.Decision) {
        const bucketCount = Math.min(n, 5);
        const bucketSize = Math.ceil(n / bucketCount);
        ctx.strokeStyle = '#14b8a6';
        ctx.lineWidth = 1;
        for (let bi = 0; bi < bucketCount; bi++) {
          const s = bi * bucketSize;
          const e = Math.min(s + bucketSize - 1, n - 1);
          if (e < s) break;
          const sx = startX + s * (bw + barGap) - barGap / 2;
          const ex = startX + e * (bw + barGap) + bw + barGap / 2;
          const y = 6 + bi * 2;
          ctx.beginPath();
          ctx.moveTo(sx, y + 4);
          ctx.lineTo(sx, y);
          ctx.lineTo(ex, y);
          ctx.lineTo(ex, y + 4);
          ctx.stroke();
        }
      }
      break;
    }

    case 'pigeonhole': {
      // Draw pigeonhole grid indicator
      if (currentIdx !== undefined && stepType === StepType.Comparison) {
        ctx.fillStyle = '#facc15';
        ctx.fillText('hole', barCenter(currentIdx), barTop(currentIdx) - 14);
      }
      break;
    }

    case 'stooge': {
      // Draw recursive third markers
      if (compareIndices.length >= 2 && stepType === StepType.Comparison) {
        const [a, b] = compareIndices;
        if (a !== undefined && b !== undefined && a < n && b < n) {
          const ax = barCenter(a), bx = barCenter(b);
          const y = baselineY + 14;
          ctx.strokeStyle = '#ef4444';
          ctx.lineWidth = 1;
          ctx.setLineDash([2, 2]);
          ctx.beginPath();
          ctx.moveTo(ax, y - 4);
          ctx.lineTo(bx, y - 4);
          ctx.stroke();
          ctx.setLineDash([]);
          ctx.fillStyle = '#ef4444';
          ctx.fillText('⅓', (ax + bx) / 2, y);
        }
      }
      break;
    }

    case 'tim': {
      // Draw run detection indicator
      if (currentIdx !== undefined && stepType === StepType.Comparison) {
        ctx.fillStyle = '#06b6d4';
        ctx.fillText('run?', barCenter(currentIdx), barTop(currentIdx) - 14);
      }
      break;
    }

    case 'library': {
      // Draw insertion slot marker
      if (currentIdx !== undefined && stepType === StepType.Decision) {
        ctx.fillStyle = '#a78bfa';
        ctx.fillText('insert slot', barCenter(currentIdx), barTop(currentIdx) - 14);
      }
      break;
    }

    case 'binary-search': {
      // Draw [lo, hi] range bracket and mid marker
      const expl = step.metadata?.explanation ?? '';
      const loMatch = expl.match(/(?:lo|left)\s*=\s*(\d+)/i);
      const hiMatch = expl.match(/(?:hi|right)\s*=\s*(\d+)/i);
      const midMatch = expl.match(/mid\s*=\s*(\d+)/i);
      const rangeMatch = expl.match(/\[\s*(\d+)\s*\.\.\s*(\d+)\s*\]/);
      const lo = loMatch ? parseInt(loMatch[1]!, 10) : rangeMatch ? parseInt(rangeMatch[1]!, 10) : -1;
      const hi = hiMatch ? parseInt(hiMatch[1]!, 10) : rangeMatch ? parseInt(rangeMatch[2]!, 10) : -1;
      if (lo >= 0 && hi >= 0 && lo < n && hi < n && lo <= hi) {
          const sx = startX + lo * (bw + barGap) - barGap / 2;
          const ex = startX + hi * (bw + barGap) + bw + barGap / 2;
          const y = 6;
          ctx.strokeStyle = '#3b82f6';
          ctx.lineWidth = 1.5;
          ctx.beginPath();
          ctx.moveTo(sx, y + 4);
          ctx.lineTo(sx, y);
          ctx.lineTo(ex, y);
          ctx.lineTo(ex, y + 4);
          ctx.stroke();
          ctx.fillStyle = '#3b82f6';
          ctx.fillText(`[${lo},${hi}]`, (sx + ex) / 2, y + 10);
      }
      if (midMatch) {
        const mid = parseInt(midMatch[1]!, 10);
        if (mid < n) {
          const mx = barCenter(mid);
          ctx.strokeStyle = '#f59e0b';
          ctx.lineWidth = 1;
          ctx.setLineDash([3, 2]);
          ctx.beginPath();
          ctx.moveTo(mx, baselineY - totalH - 4);
          ctx.lineTo(mx, baselineY + 4);
          ctx.stroke();
          ctx.setLineDash([]);
          ctx.fillStyle = '#f59e0b';
          ctx.fillText('mid', mx, baselineY + 14);
        }
      }
      break;
    }

    case 'jump-search': {
      // Draw jump arrow showing skip distance
      if (currentIdx !== undefined) {
        const x = barCenter(currentIdx);
        const y = barTop(currentIdx) - 14;
        ctx.strokeStyle = '#22d3ee';
        ctx.fillStyle = '#22d3ee';
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(x - 12, y);
        ctx.lineTo(x + 12, y);
          ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(x + 12, y);
        ctx.lineTo(x + 6, y - 4);
        ctx.lineTo(x + 6, y + 4);
        ctx.closePath();
        ctx.fill();
        ctx.fillText('jump', x, y - 8);
      }
      break;
    }

    case 'interpolation-search': {
      // Draw probe position line
      if (currentIdx !== undefined && currentIdx < n) {
        const px = barCenter(currentIdx);
        ctx.strokeStyle = '#a855f7';
        ctx.lineWidth = 1;
        ctx.setLineDash([2, 3]);
        ctx.beginPath();
        ctx.moveTo(px, baselineY - totalH - 4);
        ctx.lineTo(px, baselineY + 4);
        ctx.stroke();
        ctx.setLineDash([]);
        ctx.fillStyle = '#a855f7';
        ctx.fillText('probe', px, baselineY + 14);
      }
      break;
    }

    case 'exponential-search': {
      // Draw exponential range expansion indicator
      if (currentIdx !== undefined) {
        const x = barCenter(currentIdx);
        const y = barTop(currentIdx) - 14;
        ctx.fillStyle = '#f97316';
        ctx.fillText('2^k', x, y);
      }
      break;
    }

    case 'ternary-search': {
      // Draw two mid markers (mid1, mid2)
      const expl = step.metadata?.explanation ?? '';
      const mid1Match = expl.match(/mid1\s*=\s*(\d+)/i);
      const mid2Match = expl.match(/mid2\s*=\s*(\d+)/i);
      if (mid1Match) {
        const m1 = parseInt(mid1Match[1]!, 10);
        if (m1 < n) {
          ctx.strokeStyle = '#8b5cf6';
          ctx.lineWidth = 1;
          ctx.setLineDash([3, 2]);
          ctx.beginPath();
          ctx.moveTo(barCenter(m1), baselineY - totalH - 4);
          ctx.lineTo(barCenter(m1), baselineY + 4);
          ctx.stroke();
          ctx.setLineDash([]);
          ctx.fillStyle = '#8b5cf6';
          ctx.fillText('m1', barCenter(m1), baselineY + 14);
        }
      }
      if (mid2Match) {
        const m2 = parseInt(mid2Match[1]!, 10);
        if (m2 < n) {
          ctx.strokeStyle = '#c084fc';
          ctx.lineWidth = 1;
          ctx.setLineDash([3, 2]);
          ctx.beginPath();
          ctx.moveTo(barCenter(m2), baselineY - totalH - 4);
          ctx.lineTo(barCenter(m2), baselineY + 4);
          ctx.stroke();
          ctx.setLineDash([]);
          ctx.fillStyle = '#c084fc';
          ctx.fillText('m2', barCenter(m2), baselineY + 14);
        }
      }
      break;
    }

    case 'fibonacci-search': {
      // Draw Fibonacci index marker
      if (currentIdx !== undefined) {
        ctx.fillStyle = '#fbbf24';
        ctx.fillText('F(k)', barCenter(currentIdx), barTop(currentIdx) - 14);
      }
      break;
    }

    default:
      break;
  }

  ctx.restore();
}

// ── DSU overlay: path compression arrows + root highlighting ──────────────────
function drawDsuOverlay(
  ctx: CanvasRenderingContext2D,
  explanation: string,
  layout: DrawLayout,
): void {
  const { barGap, startX, baselineY, totalH, range, values } = layout;
  const bw = layout.barWidth;
  const txt = explanation.toLowerCase();

  // Parse Union(a,b) or find(a) from explanation
  const unionMatch = explanation.match(/union\((\d+),\s*(\d+)\)/i);
  const findMatch = explanation.match(/find\((\d+)\)/i) || explanation.match(/roota=(\d+)/i);

  ctx.save();
  ctx.font = 'bold 9px monospace';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  if (unionMatch) {
    const a = parseInt(unionMatch[1]!, 10);
    const b = parseInt(unionMatch[2]!, 10);
    if (a < values.length && b < values.length) {
      const ax = startX + a * (bw + barGap) + bw / 2;
      const bx = startX + b * (bw + barGap) + bw / 2;
      const y = baselineY - (values[a]! / range) * totalH - 20;
      ctx.strokeStyle = '#22c55e';
      ctx.fillStyle = '#22c55e';
      ctx.lineWidth = 1.5;
      ctx.setLineDash([3, 2]);
      ctx.beginPath();
      ctx.moveTo(ax, y);
      ctx.lineTo(bx, y);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillText('union', (ax + bx) / 2, y - 8);
    }
  }

  // Path compression: draw arrow from child to root
  if (txt.includes('path compression') || txt.includes('root')) {
    const rootMatch = explanation.match(/root\s*=\s*(\d+)/i);
    if (rootMatch) {
      const root = parseInt(rootMatch[1]!, 10);
      const findIdx = findMatch ? parseInt(findMatch[1]!, 10) : -1;
      if (findIdx >= 0 && findIdx < values.length && root < values.length && findIdx !== root) {
        const fromX = startX + findIdx * (bw + barGap) + bw / 2;
        const toX = startX + root * (bw + barGap) + bw / 2;
        const fromY = baselineY - (values[findIdx]! / range) * totalH;
        const toY = baselineY - (values[root]! / range) * totalH;
        ctx.strokeStyle = '#f97316';
        ctx.fillStyle = '#f97316';
        ctx.lineWidth = 2;
        ctx.setLineDash([4, 3]);
        ctx.beginPath();
        ctx.moveTo(fromX, fromY - 15);
        const midX = (fromX + toX) / 2;
        const midY = Math.min(fromY, toY) - 35;
        ctx.quadraticCurveTo(midX, midY, toX, toY - 15);
        ctx.stroke();
        ctx.setLineDash([]);
        ctx.fillText('compress', midX, midY - 6);
      }
    }
  }

  // DSU label
  ctx.fillStyle = '#22c55e';
  ctx.textAlign = 'left';
  ctx.fillText('DSU: parent[]', startX, baselineY + 18);
  ctx.restore();
}

// ── Segment Tree overlay: tree node merge arrows ──────────────────────────────
function drawSegmentTreeOverlay(
  ctx: CanvasRenderingContext2D,
  explanation: string,
  layout: DrawLayout,
): void {
  const { barGap, startX, baselineY, totalH, range, values } = layout;
  const bw = layout.barWidth;

  // Parse "Merge: tree[N] = tree[L]+tree[R]" or "Leaf: tree[N] = arr[X]"
  const mergeMatch = explanation.match(/merge:\s*tree\[(\d+)\]\s*=\s*tree\[(\d+)\]\s*\+\s*tree\[(\d+)\]/i);
  const leafMatch = explanation.match(/leaf:\s*tree\[(\d+)\]\s*=\s*arr\[(\d+)\]/i);
  const splitMatch = explanation.match(/split.*?(\d+).*?(\d+)/i);

  ctx.save();
  ctx.font = 'bold 9px monospace';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  if (mergeMatch) {
    const parent = parseInt(mergeMatch[1]!, 10);
    const left = parseInt(mergeMatch[2]!, 10);
    const right = parseInt(mergeMatch[3]!, 10);
    if (parent < values.length && left < values.length && right < values.length) {
      const px = startX + parent * (bw + barGap) + bw / 2;
      const lx = startX + left * (bw + barGap) + bw / 2;
      const rx = startX + right * (bw + barGap) + bw / 2;
      const py = baselineY - (values[parent]! / range) * totalH;
      const ly = baselineY - (values[left]! / range) * totalH;
      const ry = baselineY - (values[right]! / range) * totalH;

      ctx.strokeStyle = '#818cf8';
      ctx.fillStyle = '#818cf8';
      ctx.lineWidth = 1.5;
      ctx.setLineDash([3, 2]);
      // Left child → parent
      ctx.beginPath();
      ctx.moveTo(lx, ly - 12);
      ctx.quadraticCurveTo((lx + px) / 2, Math.min(ly, py) - 25, px, py - 12);
      ctx.stroke();
      // Right child → parent
      ctx.beginPath();
      ctx.moveTo(rx, ry - 12);
      ctx.quadraticCurveTo((rx + px) / 2, Math.min(ry, py) - 25, px, py - 12);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillText('merge', px, Math.min(ly, ry, py) - 32);
    }
  }

  if (leafMatch) {
    const nodeIdx = parseInt(leafMatch[1]!, 10);
    if (nodeIdx < values.length) {
      const x = startX + nodeIdx * (bw + barGap) + bw / 2;
      const y = baselineY - (values[nodeIdx]! / range) * totalH;
      ctx.fillStyle = '#22c55e';
      ctx.fillText('leaf', x, y - 18);
    }
  }

  if (splitMatch) {
    ctx.fillStyle = '#fbbf24';
    ctx.textAlign = 'left';
    ctx.fillText('split →', startX, baselineY + 4);
  }

  // Label
  ctx.fillStyle = '#818cf8';
  ctx.textAlign = 'left';
  ctx.fillText('SegTree: tree[]', startX, baselineY + 18);
  ctx.restore();
}

// ── Sqrt Decomposition overlay: block boundary markers ────────────────────────
function drawSqrtBlockOverlay(
  ctx: CanvasRenderingContext2D,
  explanation: string,
  layout: DrawLayout,
): void {
  const { barGap, startX, baselineY, totalH, values } = layout;
  const bw = layout.barWidth;

  // Parse block size from explanation
  const blockMatch = explanation.match(/blocksize=(\d+)/i);
  const blockSize = blockMatch ? parseInt(blockMatch[1]!, 10) : 0;
  if (blockSize <= 0) return;

  ctx.save();
  ctx.strokeStyle = '#06b6d4';
  ctx.fillStyle = '#06b6d4';
  ctx.lineWidth = 2;
  ctx.font = 'bold 8px monospace';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'bottom';

  // Draw block boundary lines
  for (let i = 0; i <= values.length; i += blockSize) {
    const x = startX + i * (bw + barGap) - barGap / 2;
    ctx.beginPath();
    ctx.setLineDash([5, 3]);
    ctx.moveTo(x, baselineY - totalH - 8);
    ctx.lineTo(x, baselineY + 4);
    ctx.stroke();
    ctx.setLineDash([]);

    const blockIdx = Math.floor(i / blockSize);
    if (i < values.length) {
      ctx.fillText(`B${blockIdx}`, x + blockSize * (bw + barGap) / 2 - barGap / 2, baselineY - totalH - 10);
    }
  }

  // Label
  ctx.fillStyle = '#06b6d4';
  ctx.textAlign = 'left';
  ctx.fillText(`Sqrt Decomp: √n=${blockSize}`, startX, baselineY + 18);
  ctx.restore();
}

// ── Mo's Algorithm overlay: L/R pointer markers ───────────────────────────────
function drawMoPointerOverlay(
  ctx: CanvasRenderingContext2D,
  explanation: string,
  layout: DrawLayout,
): void {
  const { barGap, startX, baselineY, totalH, values } = layout;
  const bw = layout.barWidth;

  // Parse current range [L, R] from explanation
  const rangeMatch = explanation.match(/current range=\[(-?\d+),\s*(-?\d+)\]/i);
  const queryMatch = explanation.match(/q(\d+):\[(\d+),\s*(\d+)\]/i);

  ctx.save();
  ctx.font = 'bold 9px monospace';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  if (rangeMatch) {
    const L = parseInt(rangeMatch[1]!, 10);
    const R = parseInt(rangeMatch[2]!, 10);

    if (L >= 0 && L < values.length) {
      const lx = startX + L * (bw + barGap) + bw / 2;
      ctx.fillStyle = '#f97316';
      ctx.strokeStyle = '#f97316';
      ctx.lineWidth = 2;
      // L pointer (downward triangle above bar)
      ctx.beginPath();
      ctx.moveTo(lx - 6, baselineY - totalH - 18);
      ctx.lineTo(lx + 6, baselineY - totalH - 18);
      ctx.lineTo(lx, baselineY - totalH - 10);
      ctx.closePath();
      ctx.fill();
      ctx.fillText('L', lx, baselineY - totalH - 24);
    }

    if (R >= 0 && R < values.length) {
      const rx = startX + R * (bw + barGap) + bw / 2;
      ctx.fillStyle = '#a855f7';
      ctx.strokeStyle = '#a855f7';
      // R pointer (downward triangle above bar)
      ctx.beginPath();
      ctx.moveTo(rx - 6, baselineY - totalH - 18);
      ctx.lineTo(rx + 6, baselineY - totalH - 18);
      ctx.lineTo(rx, baselineY - totalH - 10);
      ctx.closePath();
      ctx.fill();
      ctx.fillText('R', rx, baselineY - totalH - 24);
    }

    // Range bracket
    if (L >= 0 && R >= L && L < values.length && R < values.length) {
      const lx = startX + L * (bw + barGap);
      const rx = startX + R * (bw + barGap) + bw;
      const y = baselineY + 6;
      ctx.strokeStyle = '#fbbf24';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(lx, y + 4);
      ctx.lineTo(lx, y);
      ctx.lineTo(rx, y);
      ctx.lineTo(rx, y + 4);
      ctx.stroke();
    }
  }

  if (queryMatch) {
    const qIdx = queryMatch[1];
    ctx.fillStyle = '#fbbf24';
    ctx.textAlign = 'left';
    ctx.fillText(`Mo's Q${qIdx}`, startX, baselineY + 18);
  }

  ctx.restore();
}

// ── Bloom Filter overlay: hash function arrows + bit cell markers ─────────────
function drawBloomFilterOverlay(
  ctx: CanvasRenderingContext2D,
  explanation: string,
  layout: DrawLayout,
): void {
  const { barGap, startX, baselineY, totalH, values } = layout;
  const bw = layout.barWidth;

  ctx.save();
  ctx.font = 'bold 9px monospace';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  // Parse hash positions from explanation (e.g. "hash positions: 3, 7, 15")
  const hashMatch = explanation.match(/hash(?:es| positions)?[:\s]+([\d,\s]+)/i);
  if (hashMatch) {
    const positions = hashMatch[1]!.split(/[,\s]+/).map(s => parseInt(s.trim(), 10)).filter(n => !isNaN(n) && n >= 0 && n < values.length);
    // Draw hash arrows from a virtual "item" label to each bit position
    const labelX = startX - 20;
    const labelY = baselineY - totalH / 2;
    ctx.fillStyle = '#e879f9';
    ctx.textAlign = 'right';
    ctx.fillText('hash→', labelX, labelY);

    ctx.strokeStyle = '#e879f9';
    ctx.lineWidth = 1;
    ctx.setLineDash([2, 2]);
    for (const pos of positions) {
      const px = startX + pos * (bw + barGap) + bw / 2;
      const py = baselineY - totalH - 4;
      ctx.beginPath();
      ctx.moveTo(labelX + 5, labelY);
      ctx.lineTo(px, py);
      ctx.stroke();
    }
    ctx.setLineDash([]);
  }

  // Mark set bits with a filled circle indicator
  for (let i = 0; i < values.length; i++) {
    if (values[i] === 1) {
      const x = startX + i * (bw + barGap) + bw / 2;
      ctx.fillStyle = '#22c55e';
      ctx.beginPath();
      ctx.arc(x, baselineY - totalH - 6, 2.5, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  // Label
  ctx.fillStyle = '#e879f9';
  ctx.textAlign = 'left';
  ctx.fillText('Bloom Filter: bit[]', startX, baselineY + 18);
  ctx.restore();
}

// ── Greedy algorithm overlays ─────────────────────────────────────────────────

function drawActivitySelectionOverlay(ctx: CanvasRenderingContext2D, explanation: string, layout: DrawLayout): void {
  const { barGap, startX, baselineY, totalH, values, metadata } = layout;
  const bw = layout.barWidth;
  ctx.save();
  ctx.font = 'bold 9px monospace';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  const isSelect = /select/i.test(explanation);
  const isSkip = /skip/i.test(explanation);
  const idx = metadata.currentIndex;
  if (idx !== undefined && idx >= 0 && idx < values.length && (isSelect || isSkip)) {
    const x = startX + idx * (bw + barGap) + bw / 2;
    const y = baselineY - totalH - 14;
    if (isSelect) {
      ctx.fillStyle = '#22c55e';
      ctx.fillText('✓ SELECT', x, y);
    } else {
      ctx.fillStyle = '#ef4444';
      ctx.fillText('✗ SKIP', x, y);
    }
  }
  ctx.fillStyle = '#22c55e';
  ctx.textAlign = 'left';
  ctx.fillText('Activity Selection: finish[]', startX, baselineY + 18);
  ctx.restore();
}

function drawFracKnapsackOverlay(ctx: CanvasRenderingContext2D, explanation: string, layout: DrawLayout): void {
  const { barGap, startX, baselineY, totalH, values, metadata } = layout;
  const bw = layout.barWidth;
  ctx.save();
  ctx.font = 'bold 9px monospace';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  const takeMatch = explanation.match(/take\s*(?:all|full)/i);
  const partialMatch = explanation.match(/take\s*(\d+(?:\.\d+)?)%\s*of/i);
  const idx = metadata.currentIndex;
  if (idx !== undefined && idx >= 0 && idx < values.length && (takeMatch || partialMatch)) {
    const x = startX + idx * (bw + barGap) + bw / 2;
    const y = baselineY - totalH - 14;
    if (takeMatch) {
      ctx.fillStyle = '#22c55e';
      ctx.fillText('TAKE ALL', x, y);
    } else {
      ctx.fillStyle = '#fbbf24';
      ctx.fillText('TAKE PART', x, y);
    }
  }

  // Item labels under bars
  const labels = metadata.itemLabels as string[] | undefined;
  if (labels) {
    ctx.font = 'bold 10px monospace';
    ctx.fillStyle = '#94a3b8';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    for (let i = 0; i < labels.length && i < values.length; i++) {
      const x = startX + i * (bw + barGap) + bw / 2;
      ctx.fillText(labels[i]!, x, baselineY + 4);
    }
  }

  // Remaining capacity and total value in top-left
  ctx.font = 'bold 9px monospace';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';
  ctx.fillStyle = '#22c55e';
  ctx.fillText('Fractional Knapsack: ratio[]', startX, baselineY + 18);
  const remCap = metadata.remainingCap;
  const totVal = metadata.totalValue;
  if (remCap !== undefined && totVal !== undefined) {
    ctx.fillStyle = '#60a5fa';
    ctx.fillText(`cap: ${remCap}`, startX, baselineY + 32);
    ctx.fillStyle = '#fbbf24';
    ctx.fillText(`val: ${totVal.toFixed(1)}`, startX + 80, baselineY + 32);
  }
  ctx.restore();
}

function drawHuffmanOverlay(ctx: CanvasRenderingContext2D, explanation: string, layout: DrawLayout): void {
  const { barGap, startX, baselineY, totalH, values } = layout;
  const bw = layout.barWidth;
  ctx.save();
  ctx.font = 'bold 9px monospace';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  // Parse merge from explanation
  const mergeMatch = explanation.match(/merge\s*(\d+)\s*(?:and|&|\+)\s*(\d+)/i);
  if (mergeMatch) {
    const a = parseInt(mergeMatch[1]!, 10);
    const b = parseInt(mergeMatch[2]!, 10);
    if (a < values.length && b < values.length) {
      const ax = startX + a * (bw + barGap) + bw / 2;
      const bx = startX + b * (bw + barGap) + bw / 2;
      const y = baselineY - totalH - 14;
      ctx.strokeStyle = '#a855f7';
      ctx.fillStyle = '#a855f7';
      ctx.lineWidth = 1.5;
      ctx.setLineDash([3, 2]);
      ctx.beginPath();
      ctx.moveTo(ax, y);
      ctx.lineTo(bx, y);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillText('merge', (ax + bx) / 2, y - 8);
    }
  }
  ctx.fillStyle = '#a855f7';
  ctx.textAlign = 'left';
  ctx.fillText('Huffman: freq[]', startX, baselineY + 18);
  ctx.restore();
}

function drawJobSeqOverlay(ctx: CanvasRenderingContext2D, explanation: string, layout: DrawLayout): void {
  const { barGap, startX, baselineY, totalH, values, metadata } = layout;
  const bw = layout.barWidth;
  ctx.save();
  ctx.font = 'bold 9px monospace';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  const isAssign = /assign/i.test(explanation);
  const isSkip = /skip/i.test(explanation);
  const idx = metadata.currentIndex;
  if (idx !== undefined && idx >= 0 && idx < values.length && (isAssign || isSkip)) {
    const x = startX + idx * (bw + barGap) + bw / 2;
    if (isAssign) {
      ctx.fillStyle = '#22c55e';
      ctx.fillText('ASSIGN', x, baselineY - totalH - 14);
    } else {
      ctx.fillStyle = '#ef4444';
      ctx.fillText('SKIP', x, baselineY - totalH - 14);
    }
  }
  ctx.fillStyle = '#22c55e';
  ctx.textAlign = 'left';
  ctx.fillText('Job Sequencing: profit[]', startX, baselineY + 18);
  ctx.restore();
}

// ── Math algorithm overlays ───────────────────────────────────────────────────

function drawFastExpOverlay(ctx: CanvasRenderingContext2D, explanation: string, layout: DrawLayout): void {
  const { barGap, startX, baselineY, totalH, values } = layout;
  const bw = layout.barWidth;
  const tw = values.length * (bw + barGap);
  ctx.save();
  ctx.font = 'bold 9px monospace';
  ctx.textAlign = 'left';
  ctx.fillStyle = '#06b6d4';
  ctx.fillText('Fast Exp: result[]', startX, baselineY + 18);
  if (explanation.toLowerCase().includes('bit') || explanation.toLowerCase().includes('odd')) {
    ctx.fillStyle = '#fbbf24';
    ctx.textAlign = 'center';
    ctx.fillText('×base (odd exp)', startX + tw / 2, baselineY - totalH - 14);
  } else if (explanation.toLowerCase().includes('square') || explanation.toLowerCase().includes('half')) {
    ctx.fillStyle = '#818cf8';
    ctx.textAlign = 'center';
    ctx.fillText('base² (shift)', startX + tw / 2, baselineY - totalH - 14);
  }
  ctx.restore();
}

function drawMillerRabinOverlay(ctx: CanvasRenderingContext2D, explanation: string, layout: DrawLayout): void {
  const { barGap, startX, baselineY, totalH, values } = layout;
  const bw = layout.barWidth;
  const tw = values.length * (bw + barGap);
  ctx.save();
  ctx.font = 'bold 9px monospace';
  ctx.textAlign = 'left';
  ctx.fillStyle = '#ef4444';
  ctx.fillText('Miller-Rabin: witness test', startX, baselineY + 18);
  if (explanation.toLowerCase().includes('composite')) {
    ctx.fillStyle = '#ef4444';
    ctx.textAlign = 'center';
    ctx.fillText('COMPOSITE', startX + tw / 2, baselineY - totalH - 14);
  } else if (explanation.toLowerCase().includes('probably prime') || explanation.toLowerCase().includes('witness')) {
    ctx.fillStyle = '#22c55e';
    ctx.textAlign = 'center';
    ctx.fillText('probably prime', startX + tw / 2, baselineY - totalH - 14);
  }
  ctx.restore();
}

function drawChineseRemainderOverlay(ctx: CanvasRenderingContext2D, explanation: string, layout: DrawLayout): void {
  const { barGap, startX, baselineY, totalH, values } = layout;
  const bw = layout.barWidth;
  const tw = values.length * (bw + barGap);
  ctx.save();
  ctx.font = 'bold 9px monospace';
  ctx.textAlign = 'left';
  ctx.fillStyle = '#f472b6';
  ctx.fillText('CRT: remainders[]', startX, baselineY + 18);
  const modMatch = explanation.match(/mod\s*(\d+)/i);
  if (modMatch) {
    ctx.fillStyle = '#f472b6';
    ctx.textAlign = 'center';
    ctx.fillText(`mod ${modMatch[1]}`, startX + tw / 2, baselineY - totalH - 14);
  }
  ctx.restore();
}

function drawModArithOverlay(ctx: CanvasRenderingContext2D, explanation: string, layout: DrawLayout): void {
  const { barGap, startX, baselineY, totalH, values } = layout;
  const bw = layout.barWidth;
  const tw = values.length * (bw + barGap);
  ctx.save();
  ctx.font = 'bold 9px monospace';
  ctx.textAlign = 'left';
  ctx.fillStyle = '#a78bfa';
  ctx.fillText('Modular Arithmetic', startX, baselineY + 18);
  const modMatch = explanation.match(/%\s*(\d+)/) || explanation.match(/mod\s*(\d+)/i);
  if (modMatch) {
    ctx.fillStyle = '#a78bfa';
    ctx.textAlign = 'center';
    ctx.fillText(`mod ${modMatch[1]}`, startX + tw / 2, baselineY - totalH - 14);
  }
  ctx.restore();
}

function drawAlgorithmOverlays(
  ctx: CanvasRenderingContext2D,
  step: ExecutionStep,
  layout: DrawLayout,
  algorithmId?: string,
): void {
  if (step.payload.type !== 'array') return;
  const explanation = step.metadata?.explanation ?? '';
  if (!explanation) return;

  const algoKind = detectArrayAlgo(explanation, algorithmId);
  if (algoKind === 'fenwick') drawFenwickArrows(ctx, explanation, layout);
  else if (algoKind === 'sieve') drawSieveArcs(ctx, explanation, layout);
  else if (algoKind === 'gcd') drawGcdArrows(ctx, explanation, layout);
  else if (algoKind === 'skiplist') drawSkipListLevels(ctx, explanation, layout);
  else if (algoKind === 'fibonacci' || algoKind === 'dp') {
    const ap = step.payload as ArrayStatePayload;
    drawDpDependencyArrows(ctx, explanation, layout, ap.metadata ?? {});
  }
  else if (algoKind === 'dsu') drawDsuOverlay(ctx, explanation, layout);
  else if (algoKind === 'segment-tree') drawSegmentTreeOverlay(ctx, explanation, layout);
  else if (algoKind === 'sqrt-decomp') drawSqrtBlockOverlay(ctx, explanation, layout);
  else if (algoKind === 'mo-algo') drawMoPointerOverlay(ctx, explanation, layout);
  else if (algoKind === 'bloom') drawBloomFilterOverlay(ctx, explanation, layout);
  else if (algoKind === 'activity') drawActivitySelectionOverlay(ctx, explanation, layout);
  else if (algoKind === 'frac-knapsack') drawFracKnapsackOverlay(ctx, explanation, layout);
  else if (algoKind === 'huffman') drawHuffmanOverlay(ctx, explanation, layout);
  else if (algoKind === 'job-seq') drawJobSeqOverlay(ctx, explanation, layout);
  else if (algoKind === 'fast-exp') drawFastExpOverlay(ctx, explanation, layout);
  else if (algoKind === 'miller-rabin') drawMillerRabinOverlay(ctx, explanation, layout);
  else if (algoKind === 'chinese-remainder') drawChineseRemainderOverlay(ctx, explanation, layout);
  else if (algoKind === 'mod-arith') drawModArithOverlay(ctx, explanation, layout);
}

// ── Component ──────────────────────────────────────────────────────────────────

export const SortingVisualizer: React.FC<SortingVisualizerProps> = memo(({
  currentStep,
  darkMode: _darkMode = true,
  barWidth: propBarWidth,
  barGap = 3,
  height = 420,
  algorithmId = '',
  allSteps,
  stepIndex,
}) => {
  const canvasRef    = useRef<HTMLCanvasElement>(null);
  const rafRef       = useRef<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }

    try {
      const dpr = Math.max(1, Math.floor(window.devicePixelRatio || 1));
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      if (!currentStep || currentStep.payload.type !== 'array') {
        ctx.clearRect(0, 0, canvas.width / dpr, canvas.height / dpr);
        return;
      }
      const state    = currentStep.payload as ArrayStatePayload;
      const { items, metadata } = state;
      if (!Array.isArray(items) || items.length === 0) {
        ctx.clearRect(0, 0, canvas.width / dpr, canvas.height / dpr);
        return;
      }

      const W = canvas.width / dpr;
      const H = canvas.height / dpr;
      const itemCount = items.length;

      const availW  = W - PAD_SIDE * 2;
      const bw      = propBarWidth ?? Math.max(4, (availW - (itemCount - 1) * barGap) / itemCount);
      const totalW  = itemCount * bw + (itemCount - 1) * barGap;
      const startX  = PAD_SIDE + Math.max(0, (availW - totalW) / 2);
      const drawH   = H - PAD_TOP - PAD_BOTTOM;

      const values  = items.map((it: ArrayItem) => Number(it.value) || 0);
      const minVal  = Math.min(0, ...values);
      const maxVal  = Math.max(0, ...values);
      const range   = Math.max(1, maxVal - minVal);
      const baselineY = H - PAD_BOTTOM - ((0 - minVal) / range) * drawH;

      const activeSet = new Set<number>(
        [
          metadata.currentIndex,
          metadata.compareIndex,
          metadata.pivotIndex,
          ...(metadata.compareIndices ?? []),
          ...(metadata.highlightIndices ?? []),
        ].filter((i): i is number => typeof i === 'number' && i >= 0 && i < itemCount),
      );

      ctx.clearRect(0, 0, W, H);

      // Baseline rule (zero line, supports negative values)
      ctx.strokeStyle = 'rgba(51,65,85,0.5)';
      ctx.lineWidth   = 1;
      ctx.beginPath();
      ctx.moveTo(PAD_SIDE, baselineY);
      ctx.lineTo(W - PAD_SIDE, baselineY);
      ctx.stroke();

      const layout: DrawLayout = {
        barWidth: bw,
        barGap,
        startX,
        canvasHeight: H,
        totalH: drawH,
        minVal,
        maxVal,
        range,
        baselineY,
        values,
        activeSet,
        metadata,
      };

      // ── Detect visual mode ────────────────────────────────────────────────
      type VisualMode = 'bars' | 'tree' | 'pass-grid' | 'search';
      const detectVisualMode = (algId: string): VisualMode => {
        const id = algId.toLowerCase();
        if (id.includes('merge')) return 'tree';
        if (id.includes('bubble')) return 'pass-grid';
        if (isSearchAlgorithm(id)) return 'search';
        return 'bars';
      };
      const visualMode = detectVisualMode(algorithmId);

      // ── Pure drawing passes ───────────────────────────────────────────────
      if (visualMode === 'tree') {
        drawMergeTree(ctx, currentStep, W, H, stepIndex);
      } else if (visualMode === 'pass-grid') {
        drawBubbleSort(ctx, currentStep, W, H, allSteps, stepIndex);
      } else if (visualMode === 'search') {
        drawSearch(ctx, currentStep, W, H, algorithmId);
      } else {
        drawBars(ctx, items, bw, drawH, layout);
      }
      if (itemCount <= MAX_ITEMS_WITH_LABELS && visualMode === 'bars') {
        drawLabels(ctx, items, bw, layout);
      }
      if (itemCount <= MAX_ITEMS_WITH_ANNOTATIONS && visualMode === 'bars') {
        drawAnnotations(ctx, currentStep, bw, layout);
        const sig = detectSortSignature(algorithmId);
        if (sig !== 'none') drawSortSignature(ctx, sig, currentStep, layout);
        drawAlgorithmOverlays(ctx, currentStep, layout, algorithmId);
      }

      // ── Step explanation overlay (top-left) ──────────────────────────────
      const expl = currentStep.metadata?.explanation ?? '';
      if (expl) {
        const truncText = expl.length > 68 ? expl.slice(0, 65) + '…' : expl;
        ctx.font = '10px monospace';
        const pillW = Math.min(W - 16, 420);
        const pillH = 22;
        ctx.fillStyle = 'rgba(10,10,14,0.82)';
        ctx.strokeStyle = 'rgba(255,255,255,0.06)';
        ctx.lineWidth = 0.5;
        ctx.beginPath();
        ctx.roundRect(8, 8, pillW, pillH, 6);
        ctx.fill();
        ctx.stroke();
        ctx.fillStyle = '#a1a1aa';
        ctx.textAlign = 'left';
        ctx.textBaseline = 'middle';
        ctx.fillText(truncText, 16, 8 + pillH / 2);
      }

    } catch (frameErr) {
      console.warn('[SortingVisualizer] Frame error — retaining previous frame:', frameErr);
    }
  }, [currentStep, propBarWidth, barGap, algorithmId, allSteps, stepIndex]);

  // ── Serialised draw scheduler ─────────────────────────────────────────────
  // All draw triggers funnel through scheduleDraw() so they are batched onto
  // a single animation frame via rafRef. This eliminates the race condition
  // where ResizeObserver resets canvas.width (clearing the bitmap) while a
  // separately queued RAF from the step-change effect is still in-flight,
  // producing a one-frame blank flash on every algorithm switch.

  const scheduleDraw = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
    }
    rafRef.current = requestAnimationFrame(() => {
      draw();
      rafRef.current = null;
    });
  }, [draw]);

  // ── ResizeObserver ────────────────────────────────────────────────────────

  useEffect(() => {
    const container = containerRef.current;
    const canvas    = canvasRef.current;
    if (!container || !canvas) return;

    const sync = () => {
      const rect = container.getBoundingClientRect();
      const w    = Math.max(200, Math.round(rect.width));
      const h    = Math.max(120, Math.round(rect.height) || height);
      const dpr  = Math.max(1, Math.floor(window.devicePixelRatio || 1));
      const styleW = `${w}px`;
      const styleH = `${h}px`;
      if (canvas.style.width !== styleW || canvas.style.height !== styleH || canvas.width !== w * dpr || canvas.height !== h * dpr) {
        canvas.style.width  = styleW;
        canvas.style.height = styleH;
        canvas.width  = w * dpr;
        canvas.height = h * dpr;
      }
      scheduleDraw();
    };

    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(container);
    return () => {
      ro.disconnect();
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [scheduleDraw, height]);

  // ── Redraw on step change ─────────────────────────────────────────────────

  useEffect(() => {
    scheduleDraw();
    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [currentStep, scheduleDraw]);

  // ── Legend ─────────────────────────────────────────────────────────────────

  const legendItems: { label: string; color: string }[] = [
    { label: 'Default',    color: gradientColorAt(0.65) },
    { label: 'Sorted',     color: COLOR_SORTED  },
    { label: 'Heap',       color: COLOR_HEAP    },
    { label: 'Pivot',      color: COLOR_PIVOT   },
    { label: 'Compare',    color: COLOR_COMPARE },
    { label: 'Swap / Cur', color: COLOR_SWAP    },
  ];

  return (
    <div className="flex flex-col w-full h-full gap-2 min-h-0">
      <div ref={containerRef} className="flex-1 relative min-h-0 w-full">
        <canvas ref={canvasRef} className="absolute inset-0" style={{ display: 'block' }} />
      </div>

      <div className="flex-shrink-0 flex items-center gap-3 px-3 pb-1 flex-wrap" style={{ minHeight: 22 }}>
        {legendItems.map(({ label, color }) => (
          <div key={label} className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: color }} />
            <span className="text-[10px] font-mono" style={{ color: '#52525b' }}>{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
});

export default SortingVisualizer;
